import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
    FileWarning, Eye, CheckCircle, XCircle, 
    Calendar, User, MapPin, Loader2, RefreshCw, 
    Mail, Send, CheckCircle2, X 
} from 'lucide-react';

const ReportsPage = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    // --- State Yönetimi ---
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all'); // all, pending, resolved
    
    // Modal Kontrolleri
    const [selectedReport, setSelectedReport] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    // İşlem Durumları
    const [updating, setUpdating] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    // E-posta Form State
    const [emailSubject, setEmailSubject] = useState("");
    const [emailBody, setEmailBody] = useState("");

    // --- Veri Çekme ---
    useEffect(() => {
        fetchReports();
    }, [filterStatus]);

    const fetchReports = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${API_URL}/api/admin/reports`, {
                params: { status: filterStatus },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setReports(response.data);
        } catch (error) {
            console.error("Raporlar çekilemedi", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Modal Açma/Kapama İşlemleri ---
    
    const openDetailModal = (report) => {
        setSelectedReport(report);
        setIsDetailModalOpen(true);
    };

    const closeDetailModal = () => {
        setIsDetailModalOpen(false);
        // selectedReport'u hemen null yapmıyoruz ki animasyon sırasında veri kaybolmasın
    };

    const openEmailModal = (report) => {
        // Detay modalı açıksa kapatalım, e-posta modalına geçelim
        setIsDetailModalOpen(false);
        
        setSelectedReport(report);
        setEmailSubject("Mekânname Tahmin Bildirimi Hakkında");
        setEmailBody(`Merhaba ${report.user_name},\n\n"${report.location}" konumu için bildirdiğiniz geri bildirimi inceledik. Katkılarınız için teşekkür ederiz.\n\nSaygılarımızla,\nMekânname Ekibi`);
        setIsEmailModalOpen(true);
    };

    const closeEmailModal = () => {
        setIsEmailModalOpen(false);
    };

    // --- Aksiyonlar ---

    // 1. Sadece Statü Güncelleme (Email atmadan)
    const updateStatusOnly = async (newStatus) => {
        setUpdating(true);
        const token = localStorage.getItem('token');
        try {
            await axios.patch(`${API_URL}/api/admin/reports/${selectedReport.id}`, 
                { status: newStatus },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            // Listeyi güncelle
            setReports(reports.map(r => r.id === selectedReport.id ? { ...r, status: newStatus } : r));
            
            // Başarı mesajı göster (Kısa süreliğine veya modal kapat)
            closeDetailModal();
        } catch (error) {
            console.error("Güncelleme hatası", error);
            alert("Durum güncellenemedi.");
        } finally {
            setUpdating(false);
        }
    };

    // 2. E-posta Gönderme ve Statüyü "Resolved" Yapma
    const handleSendEmail = async (e) => {
        e.preventDefault();
        if (!emailSubject || !emailBody) return;

        setSendingEmail(true);
        const token = localStorage.getItem('token');

        try {
            // A) E-posta Gönder
            await axios.post(`${API_URL}/api/admin/send_feedback_email`, {
                to: selectedReport.user_email,
                subject: emailSubject,
                body: emailBody
            }, { headers: { 'Authorization': `Bearer ${token}` } });

            // B) Rapor Statüsünü "Resolved" yap
            await axios.patch(`${API_URL}/api/admin/reports/${selectedReport.id}`, 
                { status: 'resolved' },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            // C) Listeyi Güncelle
            setReports(reports.map(r => r.id === selectedReport.id ? { ...r, status: 'resolved' } : r));

            // D) Modalları Yönet
            closeEmailModal();
            setSuccessMessage(`E-posta ${selectedReport.user_email} adresine gönderildi ve rapor çözüldü olarak işaretlendi.`);
            setIsSuccessModalOpen(true);

        } catch (error) {
            console.error("İşlem hatası:", error);
            alert("E-posta gönderilirken bir hata oluştu.");
        } finally {
            setSendingEmail(false);
        }
    };

    // Yardımcı: Tarih Formatı
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('tr-TR', {
            day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
        }).format(date);
    };

    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 p-6 md:p-12 font-sans relative">
            
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-8 border-b border-gray-800 pb-6 flex flex-col md:flex-row justify-between items-end gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
                        Raporlar & Bildirimler
                    </h1>
                    <p className="text-gray-400 mt-2 text-sm">
                        Kullanıcı bildirimlerini inceleyin, durumlarını güncelleyin ve geri bildirim gönderin.
                    </p>
                </div>
                
                <div className="flex items-center gap-3">
                    <div className="bg-gray-800 p-1 rounded-lg border border-gray-700 flex">
                        <button onClick={() => setFilterStatus('all')} className={`px-4 py-2 text-xs font-medium rounded-md transition-colors ${filterStatus === 'all' ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-400 hover:text-white'}`}>Tümü</button>
                        <button onClick={() => setFilterStatus('pending')} className={`px-4 py-2 text-xs font-medium rounded-md transition-colors ${filterStatus === 'pending' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'text-gray-400 hover:text-white'}`}>Bekleyenler</button>
                        <button onClick={() => setFilterStatus('resolved')} className={`px-4 py-2 text-xs font-medium rounded-md transition-colors ${filterStatus === 'resolved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'text-gray-400 hover:text-white'}`}>Çözülenler</button>
                    </div>
                    <button onClick={() => navigate('/admin/dashboard')} className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium border border-gray-700 transition-colors">Geri</button>
                </div>
            </div>

            {/* Tablo */}
            <div className="max-w-7xl mx-auto bg-gray-800 rounded-2xl border border-gray-700 shadow-xl overflow-hidden min-h-[400px]">
                {loading ? (
                    <div className="h-full flex flex-col items-center justify-center p-20 text-gray-500">
                        <Loader2 className="animate-spin h-10 w-10 mb-4 text-orange-500" />
                        <span>Raporlar yükleniyor...</span>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-400">
                            <thead className="bg-gray-900/50 text-gray-500 uppercase font-bold text-xs tracking-wider border-b border-gray-700">
                                <tr>
                                    <th className="px-6 py-4">ID / Yer</th>
                                    <th className="px-6 py-4">Kullanıcı</th>
                                    <th className="px-6 py-4">Tarih</th>
                                    <th className="px-6 py-4 text-center">Durum</th>
                                    <th className="px-6 py-4 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/50">
                                {reports.map((report) => (
                                    <tr key={report.id} className="hover:bg-gray-700/20 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-gray-700 rounded-lg text-orange-400">
                                                    <FileWarning size={18} />
                                                </div>
                                                <div>
                                                    <div className="font-bold text-gray-200">#{report.id}</div>
                                                    <div className="text-xs text-orange-300 font-medium">{report.location}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-gray-200">{report.user_name}</span>
                                                <span className="text-xs text-gray-500">{report.user_email}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-mono text-xs">
                                            {formatDate(report.created_at)}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                                report.status === 'resolved'
                                                    ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                                    : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                                            }`}>
                                                {report.status === 'resolved' ? 'Çözüldü' : 'İnceleniyor'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => openEmailModal(report)}
                                                    className="p-2 text-indigo-400 hover:text-white hover:bg-indigo-600 rounded-lg transition-colors"
                                                    title="E-posta Gönder"
                                                >
                                                    <Mail size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => openDetailModal(report)}
                                                    className="text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1"
                                                >
                                                    <Eye size={14} /> Detay
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {reports.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-gray-500 italic">Görüntülenecek rapor bulunamadı.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* --- DETAY MODALI --- */}
            {isDetailModalOpen && selectedReport && (
                <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeDetailModal}></div>
                    <div className="relative bg-gray-800 rounded-2xl border border-gray-600 shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
                        
                        <div className="bg-gray-900/50 p-6 border-b border-gray-700 flex justify-between items-start">
                            <div>
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <FileWarning className="text-orange-500" />
                                    Rapor Detayı <span className="text-gray-500 text-lg">#{selectedReport.id}</span>
                                </h3>
                                <p className="text-gray-400 text-sm mt-1">Rapor detaylarını inceleyin.</p>
                            </div>
                            <button onClick={closeDetailModal} className="text-gray-500 hover:text-white"><XCircle size={24} /></button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="flex items-start gap-3 bg-gray-700/30 p-4 rounded-xl border border-gray-700">
                                <MapPin className="text-teal-400 mt-1 shrink-0" />
                                <div>
                                    <span className="text-xs uppercase font-bold text-gray-500">Konum</span>
                                    <p className="text-lg text-white font-medium">{selectedReport.location}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-700/20 p-3 rounded-lg border border-gray-700">
                                    <div className="flex items-center gap-2 mb-1">
                                        <User size={14} className="text-indigo-400" />
                                        <span className="text-xs font-bold text-gray-500 uppercase">Kullanıcı</span>
                                    </div>
                                    <div className="text-sm text-gray-200">{selectedReport.user_name}</div>
                                    <div className="text-xs text-gray-500 truncate">{selectedReport.user_email}</div>
                                </div>
                                <div className="bg-gray-700/20 p-3 rounded-lg border border-gray-700">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Calendar size={14} className="text-indigo-400" />
                                        <span className="text-xs font-bold text-gray-500 uppercase">Tarih</span>
                                    </div>
                                    <div className="text-sm text-gray-200">{formatDate(selectedReport.created_at)}</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-900/80 p-6 border-t border-gray-700 flex justify-between gap-3">
                             <button 
                                onClick={() => openEmailModal(selectedReport)}
                                className="px-4 py-2 rounded-lg text-sm font-bold bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg flex items-center gap-2"
                            >
                                <Mail className="w-4 h-4" /> Kullanıcıya Yanıtla
                            </button>

                            <div className="flex gap-2">
                                {selectedReport.status !== 'resolved' ? (
                                    <button 
                                        onClick={() => updateStatusOnly('resolved')}
                                        disabled={updating}
                                        className="px-4 py-2 rounded-lg text-sm font-bold bg-green-600 hover:bg-green-500 text-white shadow-lg flex items-center gap-2"
                                    >
                                        {updating ? <RefreshCw className="animate-spin w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                        Çözüldü İşaretle
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => updateStatusOnly('pending')}
                                        disabled={updating}
                                        className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-600 text-gray-300 hover:bg-gray-700"
                                    >
                                        Tekrar Aç
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- E-POSTA GÖNDERME MODALI --- */}
            {isEmailModalOpen && selectedReport && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeEmailModal}></div>
                    <div className="relative bg-gray-800 rounded-2xl border border-gray-600 shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in duration-200">
                        <div className="bg-gray-900/50 p-6 border-b border-gray-700 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <Mail className="text-indigo-400" /> Yanıt Gönder
                            </h2>
                            <button onClick={closeEmailModal} className="text-gray-400 hover:text-white"><X size={24} /></button>
                        </div>
                        
                        <form onSubmit={handleSendEmail} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Alıcı</label>
                                <input type="text" value={selectedReport.user_email} readOnly className="w-full p-2.5 bg-gray-900 border border-gray-700 rounded-lg text-gray-300 focus:outline-none cursor-not-allowed" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Konu</label>
                                <input 
                                    type="text" 
                                    value={emailSubject}
                                    onChange={(e) => setEmailSubject(e.target.value)}
                                    className="w-full p-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mesaj İçeriği</label>
                                <textarea 
                                    rows="6"
                                    value={emailBody}
                                    onChange={(e) => setEmailBody(e.target.value)}
                                    className="w-full p-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                                ></textarea>
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={closeEmailModal} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium">İptal</button>
                                <button 
                                    type="submit" 
                                    disabled={sendingEmail}
                                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition-colors text-sm font-bold shadow-lg shadow-indigo-900/20"
                                >
                                    {sendingEmail ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                                    {sendingEmail ? "Gönderiliyor..." : "Gönder & Çözüldü Yap"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- BAŞARI MODALI --- */}
            {isSuccessModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
                    <div className="relative bg-gray-800 rounded-2xl p-8 max-w-sm w-full shadow-2xl text-center border border-gray-700 animate-in zoom-in duration-300">
                        <CheckCircle2 size={64} className="text-green-500 mx-auto mb-4 drop-shadow-lg" />
                        <h2 className="text-2xl font-bold text-white mb-2">Başarılı!</h2>
                        <p className="text-gray-400 mb-6">{successMessage}</p>
                        <button 
                            onClick={() => setIsSuccessModalOpen(false)}
                            className="w-full py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors font-bold shadow-lg"
                        >
                            Tamam
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportsPage;